/* Native scrolling stays available without JavaScript; controls never autoplay. */
(() => {
  const galleries = new Map();

  function setupGallery(root) {
    if (root.dataset.mmHeroGalleryReady) return;
    const track = root.querySelector('[data-hero-track]');
    const controls = root.querySelector('[data-hero-controls]');
    const previous = root.querySelector('[data-hero-prev]');
    const next = root.querySelector('[data-hero-next]');
    const count = root.querySelector('[data-hero-count]');
    const status = root.querySelector('[data-hero-status]');
    if (!track || !controls || !previous || !next || !count || !status) return;
    const slides = [...track.children].filter(slide => slide.matches('[data-hero-slide]'));
    const dots = [...root.querySelectorAll('[data-hero-dot]')];
    if (!slides.length) return;

    const listeners = new AbortController();
    const motion = window.matchMedia('(prefers-reduced-motion: reduce)');
    let currentIndex = 0;
    let requestedIndex = null;
    let settleTimer;
    let resizeFrame;
    let resizeObserver;
    let lastGeometry;
    root.dataset.mmHeroGalleryReady = 'true';

    const clamp = index => Math.max(0, Math.min(slides.length - 1, index));
    const leftOf = index => slides[index].getBoundingClientRect().left - slides[0].getBoundingClientRect().left;
    const geometry = () => [track.clientWidth, ...slides.map(slide => slide.getBoundingClientRect().width)].join(':');

    function destroy() {
      clearTimeout(settleTimer);
      cancelAnimationFrame(resizeFrame);
      resizeObserver?.disconnect();
      listeners.abort();
      controls.hidden = true;
      slides.forEach(slide => slide.removeAttribute('aria-hidden'));
      delete root.dataset.mmHeroGalleryReady;
      galleries.delete(root);
    }

    function connected() {
      if (root.isConnected) return true;
      destroy();
      return false;
    }

    function nearestIndex() {
      let nearest = 0;
      let distance = Infinity;
      slides.forEach((slide, index) => {
        const difference = Math.abs(track.scrollLeft - leftOf(index));
        if (difference < distance) {
          nearest = index;
          distance = difference;
        }
      });
      return nearest;
    }

    function display(index, announce) {
      const changed = currentIndex !== index;
      currentIndex = index;
      count.textContent = `${index + 1} / ${slides.length}`;
      previous.disabled = index === 0;
      next.disabled = index === slides.length - 1;
      dots.forEach(dot => {
        const dotIndex = Number(dot.dataset.heroDot);
        dot.setAttribute('aria-current', String(dotIndex === index));
      });
      slides.forEach((slide, slideIndex) => slide.setAttribute('aria-hidden', String(slideIndex !== index)));
      if (announce && changed) status.textContent = `Slide ${index + 1} of ${slides.length}`;
    }

    function settle() {
      if (!connected()) return;
      clearTimeout(settleTimer);
      requestedIndex = null;
      display(nearestIndex(), true);
    }

    function scheduleSettle() {
      if (!connected()) return;
      clearTimeout(settleTimer);
      // Also handles browsers without scrollend and a click on the current dot.
      settleTimer = setTimeout(settle, 160);
    }

    function moveTo(index, immediate = false) {
      if (!connected()) return;
      requestedIndex = clamp(index);
      track.scrollTo({ left:leftOf(requestedIndex), behavior:immediate || motion.matches ? 'instant' : 'smooth' });
      scheduleSettle();
    }

    function realign() {
      if (!connected()) return;
      cancelAnimationFrame(resizeFrame);
      resizeFrame = requestAnimationFrame(() => {
        if (!connected()) return;
        const nextGeometry = geometry();
        if (nextGeometry === lastGeometry) return;
        lastGeometry = nextGeometry;
        // Keep the selected photograph when its width changes at a breakpoint.
        moveTo(requestedIndex ?? currentIndex, true);
        settle();
      });
    }

    const options = { signal:listeners.signal };
    previous.addEventListener('click', () => moveTo((requestedIndex ?? currentIndex) - 1), options);
    next.addEventListener('click', () => moveTo((requestedIndex ?? currentIndex) + 1), options);
    dots.forEach(dot => dot.addEventListener('click', () => {
      const index = Number(dot.dataset.heroDot);
      if (Number.isInteger(index) && index >= 0 && index < slides.length) moveTo(index);
    }, options));
    track.addEventListener('keydown', event => {
      // Controls retain their native keyboard behavior; only the track handles arrows.
      if (event.target !== track || event.altKey || event.ctrlKey || event.metaKey) return;
      const index = requestedIndex ?? currentIndex;
      const destinations = { ArrowLeft:index - 1, ArrowRight:index + 1, Home:0, End:slides.length - 1 };
      if (!Object.hasOwn(destinations, event.key)) return;
      event.preventDefault();
      moveTo(destinations[event.key]);
    }, options);
    track.addEventListener('scroll', scheduleSettle, { ...options, passive:true });
    track.addEventListener('scrollend', settle, options);
    window.addEventListener('resize', realign, options);
    motion.addEventListener('change', () => {
      if (motion.matches && requestedIndex !== null) moveTo(requestedIndex, true);
    }, options);

    lastGeometry = geometry();
    if ('ResizeObserver' in window) {
      resizeObserver = new ResizeObserver(realign);
      resizeObserver.observe(track);
      slides.forEach(slide => resizeObserver.observe(slide));
    }
    display(nearestIndex(), false);
    controls.hidden = false;
    galleries.set(root, destroy);
  }

  function initialize(root = document) {
    if (root.matches?.('[data-mm-hero-gallery]')) setupGallery(root);
    root.querySelectorAll('[data-mm-hero-gallery]').forEach(setupGallery);
  }

  initialize();
  document.addEventListener('shopify:section:load', event => initialize(event.target));
  document.addEventListener('shopify:section:unload', event => {
    galleries.forEach((destroy, root) => {
      if (event.target === root || event.target.contains(root)) destroy();
    });
  });
})();
